import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { DUPLICATE_REQUEST_REPORTER } from '../config/duplicate-request-reporter.token';
import { DuplicateRequestDetectorService } from '../services/duplicate-request-detector.service';
import { currentRoute } from '../utils/current-route';
import { extractPayloadShape } from '../utils/extract-payload-shape';
import { isSameOriginUrl } from '../utils/is-same-origin-url';
import { stableStringify } from '../utils/stable-stringify';

function stripUrlQuery(url: string): string {
	return url.split('?')[0];
}

function getPageOrigin(): string {
	return globalThis.location?.origin ?? '';
}

export const duplicateRequestHttpInterceptor: HttpInterceptorFn = (request, next) => {
	if (!isSameOriginUrl(request.urlWithParams, getPageOrigin())) {
		return next(request);
	}

	const detector = inject(DuplicateRequestDetectorService);
	const reporter = inject(DUPLICATE_REQUEST_REPORTER);
	const router = inject(Router, { optional: true });
	const sanitizedUrl = stripUrlQuery(request.urlWithParams);

	try {
		const match = detector.track({
			source: 'http',
			fingerprint: `${request.method}:${request.urlWithParams}:${stableStringify(request.body)}`,
			groupingKey: sanitizedUrl,
			url: sanitizedUrl,
			method: request.method,
			route: currentRoute(router),
			timestamp: performance.now(),
			payloadShape: extractPayloadShape(request.body),
			sameOperationKey: `${request.method}:${sanitizedUrl}`,
		});

		if (match) {
			reporter.report(match);
		}
	} catch (error) {
		// Monitoring must never break the underlying request. Swallow and log instead.
		// eslint-disable-next-line no-console
		console.error('[DuplicateRequestMonitoring] http interceptor failed', error);
	}

	return next(request);
};
